import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * מוצרים ו"הכר את המוצר" (PRD 5.7).
 *
 * הצד של העובד: ארכיון שאפשר לחפש בו, ודף מוצר אחד. הפרסום עצמו מגיע
 * אליו כהודעה בערוץ, ולא כמסך נפרד.
 */

export type Product = {
  id: string;
  name: string;
  name_en: string | null;
  sku: string | null;
  category_id: string | null;
  description: string | null;
  selling_points: string | null;
  image_path: string | null;
  launched_at: string | null;
  is_active: boolean;
  product_categories: { name: string } | null;
  /** כל המחלקות של המוצר (מיגרציה 089). category_id הוא רק הראשונה. */
  product_category_links: { product_categories: { id: string; name: string } | null }[];
};

export type Spotlight = {
  spotlight_id: string;
  product_id: string;
  name: string;
  headline: string | null;
  image_path: string | null;
  quiz_id: string | null;
  published_at: string;
  quiz_passed: boolean;
};

const SELECT = `
  id, name, name_en, sku, category_id, description, selling_points,
  image_path, launched_at, is_active,
  product_categories ( name ),
  product_category_links ( product_categories ( id, name ) )
`;

export const PRODUCTS_KEY = ['products'] as const;

export function useProducts() {
  return useQuery({
    queryKey: PRODUCTS_KEY,
    queryFn: async (): Promise<Product[]> => {
      const { data, error } = await supabase
        .from('products')
        .select(SELECT)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return (data ?? []) as unknown as Product[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export type CatalogRow = {
  id: string;
  name: string;
  name_en: string | null;
  sku: string | null;
  image_path: string | null;
  is_active: boolean;
  categories: { id: string; name: string }[];
};

export const CATALOG_PAGE_SIZE = 40;

/**
 * הקטלוג לעובד: חיפוש ועימוד בשרת (מיגרציה 089).
 *
 * קודם ירד כל הקטלוג לטלפון, כולל התיאורים ונקודות המכירה, רק כדי
 * לסנן אותו בדפדפן. עם אלפי מוצרים מקומקס זה מגילת נייר שלמה בכל
 * כניסה למסך, על רשת סלולרית, בשביל לראות עשר שורות.
 */
export function useCatalog(term: string, categoryId: string | null, page: number) {
  return useQuery({
    queryKey: [...PRODUCTS_KEY, 'catalog', term, categoryId, page],
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<{ total: number; rows: CatalogRow[] }> => {
      const { data, error } = await supabase.rpc('product_search', {
        p_term: term.trim() || undefined,
        p_category: categoryId ?? undefined,
        p_include_inactive: false,
        p_limit: CATALOG_PAGE_SIZE,
        p_offset: page * CATALOG_PAGE_SIZE,
      });
      if (error) throw error;
      return (data ?? { total: 0, rows: [] }) as unknown as { total: number; rows: CatalogRow[] };
    },
  });
}

/** המחלקות, לשורת הסינון במסך המוצרים. */
export function useCatalogCategories() {
  return useQuery({
    queryKey: [...PRODUCTS_KEY, 'categories'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_categories')
        .select('id, name')
        .order('sort_order')
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useProduct(productId: string) {
  return useQuery({
    queryKey: [...PRODUCTS_KEY, productId],
    enabled: Boolean(productId),
    queryFn: async (): Promise<Product | null> => {
      const { data, error } = await supabase
        .from('products')
        .select(SELECT)
        .eq('id', productId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as Product | null;
    },
  });
}

/** החידון שמחובר למוצר, אם יש פרסום שכולל אחד. */
export function useProductQuiz(productId: string) {
  return useQuery({
    queryKey: [...PRODUCTS_KEY, productId, 'quiz'],
    enabled: Boolean(productId),
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from('product_spotlights')
        .select('quiz_id')
        .eq('product_id', productId)
        .not('published_at', 'is', null)
        .not('quiz_id', 'is', null)
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.quiz_id ?? null;
    },
  });
}

/** "הכר את המוצר" האחרון שרלוונטי לעובד, למסך הבית. */
export function useCurrentSpotlight() {
  return useQuery({
    queryKey: [...PRODUCTS_KEY, 'current-spotlight'],
    queryFn: async (): Promise<Spotlight | null> => {
      const { data, error } = await supabase.rpc('current_spotlight');
      if (error) throw error;
      return (data as Spotlight | null) ?? null;
    },
    staleTime: 60 * 1000,
  });
}


/** כתובת חתומה לתמונת מוצר בבדל public-media. */
export function useImageUrl(path: string | null | undefined) {
  return useQuery({
    queryKey: ['public-media', path],
    enabled: Boolean(path),
    staleTime: 50 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.storage
        .from('public-media')
        .createSignedUrl(path as string, 3600);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}
